import { Loading } from "app/components";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { Fragment, useEffect, useRef, useState } from "react";
import { bodyTemplate } from "app/utils/datatable.service";
import {
    deleteDocument,
    getAllDocumentList,
    getUserDocumentList,
} from "./my-document.service";
import { getSessionUserInformation } from "app/utils/utility.service";
import MyDocument from "./MyDocument";
import { pdfTemplate, pdfTemplateAuthenticated } from "app/utils/datatable.service";
import { mandatoryTemplate } from "app/utils/datatable.service";
import { getSingleUser } from "../my-profile/my-profile.service";
import { mandatoryTemplateForDocuments } from "app/utils/datatable.service";
import { validateUploadResource } from "app/utils/uploadResourceConfig";

const MyDocumentList = ({
    currentRecord,
    refreshUser,
    refreshDocumentList,
    setCheckChanges,
    checkChanges,
    onComplete,
}) => {
    const toast = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [documentModal, setDocumentModal] = useState(false);
    const [localUser, setLocalUser] = useState(null);
    const [list, setList] = useState([]);
    const [listOfDocuments, setListOfDocuments] = useState([]);
    const [allDocumentList, setAllDocumentList] = useState([]);
    const [rowData, setRowData] = useState(null);
    const [headerObj, setHeaderObj] = useState({});
    const sessionValues = getSessionUserInformation();

    // Use parent's currentRecord when provided to avoid duplicate getSingleUser; otherwise keep local fetch
    const user = currentRecord ?? localUser;

    // Wait for user before showing table so required (*) is correct from first paint (e.g. Government Employees doc when user.currentlyWorking)
    const tableReady = user !== null;

    const fetchUser = async () => {
        if (!sessionValues?.id) return;
        setIsLoading(true);
        try {
            const data = await getSingleUser(sessionValues.id);
            const merged = data ? { ...sessionValues, ...data } : null;
            setLocalUser(merged);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete?")) {
            const deletedRecord = await deleteDocument(id);
            if (deletedRecord) {
                fetchUserDocumentList();
                refreshDocumentList?.();
                alert("Record deleted successfully");
                return;
            } else {
                alert("Something went wrong, please try again later");
                return;
            }
        }
    };

    const handleEdit = (data) => {
        // SECURITY: Deny by default — existing rows: id + fileType; new rows: allowlist (see uploadResourceConfig)
        const access = validateUploadResource(data);
        if (!access.allowed) {
            const msg = access.error ?? "This resource is not allowed for upload.";
            if (toast?.current?.show) {
                toast.current.show({
                    severity: "error",
                    summary: "Access denied",
                    detail: msg,
                });
            } else {
                alert(msg);
            }
            return;
        }
        setRowData(data);
        setDocumentModal(true);
    };

    const actionBodyTemplate = (rowData) => {
        return (
                <Button
                    type="button"
                    onClick={(e) => {
                        // Stop propagation to prevent row click
                        if (e) {
                            e.stopPropagation();
                        }
                        // Call handleEdit directly
                        handleEdit(rowData);
                    }}
                    className="p-button p-button-primary mr-2"
                >
                    Edit
                </Button>
        );
    };

    const handleApply = async (
        id,
        advertisement_number,
        start_date,
        examination_name
    ) => {
        // Fetch Vacancy List
        // setIsLoading(true);
        // const record = await getVacancies(id);
        // if (record && record.length > 0) {
        //     record.map((li, index) => (li.srNo = index + 1));
        //     setVacancyList(record);
        //     setHeaderObj({
        //         advertisement_number,
        //         start_date,
        //         examination_name,
        //     });
        //     setShowVacancy(true);
        //     setIsLoading(false);
        // } else {
        //     setHeaderObj({});
        //     setShowVacancy(false);
        //     alert("No Vacancy Present");
        //     setIsLoading(false);
        // }
    };

    const fetchUserDocumentList = async () => {
        // setIsLoading(true);

        let list = await getUserDocumentList(sessionValues?.id);
        if (list.length > 0) {
            list.map((li, index) => (li.srNo = index + 1));
            setList(list);
        } else {
            setList([]);
        }
        return;
    };


    const handleDocumentModal = (shouldTriggerCheckChanges = false) => {
        if (documentModal) {
            // Modal is closing - clear rowData and refresh list
            setRowData(null);
            fetchUserDocumentList();
        }
        // If opening modal, don't clear rowData (it might be set by handleEdit)
        setDocumentModal(!documentModal);
        // Don't trigger checkChanges - it causes navigation to next step
        // The document list refresh is sufficient
    };

    // Refresh document list when parent signals checkChanges (e.g. after upload elsewhere)
    useEffect(() => {
        if (checkChanges) {
            fetchUserDocumentList();
            refreshDocumentList?.();
        }
    }, [checkChanges]);

    // On mount: load document list; only fetch user if parent did not provide currentRecord
    useEffect(() => {
        fetchUserDocumentList();
        // if (!currentRecord) {
            fetchUser();
        // }
    }, []);

    const renderInputdocumentModal = () => {
        return (
            <MyDocument
                documentModal={documentModal}
                handleDocumentModal={handleDocumentModal}
                sessionValues={sessionValues}
                onUploadSuccess={() => {
                    fetchUserDocumentList();
                    refreshDocumentList?.();
                }}
                rowData={rowData}
            />
        );
    };

    // Validate that all mandatory documents are uploaded
    const validateMandatoryDocuments = () => {
        const missingMandatoryDocs = [];
        const forceMandatoryDocs = new Set([
            "Self-attested copy of State Board Certificates",
        ]);

        list.forEach((doc) => {
            // Check if document is mandatory
            const isMandatory =
                doc.is_mandatory === "Yes" || forceMandatoryDocs.has(doc.documentName);
            
            // Special case: Government Employees document is mandatory if user is currently working
            const isSpecialMandatory = 
                doc.documentName === "Government Employees- Appointment Letter/ ID Card" &&
                localUser?.currentlyWorking && localUser?.chooseWorkStatus !== "0";
            if ((isMandatory || isSpecialMandatory) && !doc.fileName) {
                missingMandatoryDocs.push(doc.documentName);
            }
        });

        return missingMandatoryDocs;
    };

    const handleSubmit = () => {
        // Validate mandatory documents before allowing navigation
        const missingDocs = validateMandatoryDocuments();
        
        if (missingDocs.length > 0) {
            const docNames = missingDocs.join(", ");
            toast.current?.show({
                severity: "error",
                summary: "Missing Required Documents",
                detail: `Please upload all mandatory documents marked with (*). Missing: ${docNames}`,
                life: 5000,
            });
            return;
        }

        // All mandatory documents are uploaded, proceed to next step
        if (onComplete) {
            onComplete();
        }
    };

    return (
        <Fragment>
            <div className="card">
                <Card className="mb-4" title="Please upload all the required documents marked with (*)">
                    <div className="grid table-demo">
                        <Toast ref={toast} className="ToastMessage" />
                        {isLoading && (
                            <div className="spinner inner-spinner">
                                <Loading />
                            </div>
                        )}
                        <div className="col-12 lg:col-12 md:col-12">
                            <div className="card">
                                {/* <Button
                                    onClick={handleDocumentModal}
                                    label="Add New"
                                    className="p-button-raised p-button-warning mb-3"
                                /> */}
                                {!tableReady ? (
                                    <div className="spinner inner-spinner">
                                        <Loading />
                                    </div>
                                ) : (
                                <DataTable
                                    value={list}
                                    scrollable
                                    scrollHeight="600px"
                                    scrollDirection="both"
                                    className="p-datatable-gridlines p-datatable-striped p-datatable-sm p-datatable-customers"
                                    rows={10}
                                    dataKey="id"
                                    paginator
                                    rowHover
                                    selectionMode={null}
                                    // globalFilter={globalFilter}
                                    emptyMessage="No list found."
                                    loading={isLoading}
                                    // header={tableHeader}
                                >
                                    <Column
                                        style={{
                                            flexGrow: 1,
                                            flexBasis: "50px",
                                        }}
                                        field="srNo"
                                        header="Sr. No."
                                        sortable
                                        body={bodyTemplate}
                                    ></Column>
                                    
                                    <Column
                                        style={{
                                            flexGrow: 1,
                                            flexBasis: "100px",
                                        }}
                                        field="documentName"
                                        header="Name"
                                        sortable
                                        body={(rowData, options) => mandatoryTemplateForDocuments(rowData, options, localUser)}
                                    ></Column>
                                     <Column
                                        style={{
                                            flexGrow: 1,
                                            flexBasis: "50px",
                                        }}
                                        field="description"
                                        header="Description"
                                        sortable
                                        body={bodyTemplate}
                                    ></Column>
                                    <Column
                                        style={{
                                            flexGrow: 1,
                                            flexBasis: "100px",
                                        }}
                                        field="fileSize"
                                        header="Max File Size Allowed (In MB)"
                                        sortable
                                        body={bodyTemplate}
                                    ></Column>
                                    <Column
                                        style={{
                                            flexGrow: 1,
                                            flexBasis: "100px",
                                        }}
                                        field="fileName"
                                        header="File Name"
                                        sortable
                                        body={(data) =>
                                            pdfTemplateAuthenticated(
                                                data,
                                                "File Name",
                                                "fetchDocumentsPDF"
                                            )
                                        }
                                    ></Column>
                                    <Column
                                        style={{
                                            flexGrow: 1,
                                            flexBasis: "100px",
                                        }}
                                        field="status"
                                        header="Action"
                                        body={actionBodyTemplate}
                                        onClick={(e) => {
                                            e?.stopPropagation?.();
                                        }}
                                    >                                    </Column>
                                </DataTable>
                                )}
                            </div>
                        </div>
                        {documentModal && rowData && renderInputdocumentModal()}
                        {/* {displayConfirmation && (
                <ConfirmationDialogBox
                    handleCancelConfirmation={handleCancelConfirmation}
                    handleConfirmation={handleConfirmation}
                    displayConfirmation={displayConfirmation}
                />
            )} */}
                    </div>
                    {/* Submit Button to navigate to next step */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                            marginBottom: "20px",
                        }}
                    >
                        <Button
                            type="button"
                            label="Submit"
                            className="p-button-lg p-button-success"
                            onClick={handleSubmit}
                        />
                    </div>
                </Card>
            </div>
        </Fragment>
    );
};

export default MyDocumentList;
